/// <amd-module name="Feed/Common/Protocols/Maven/RestClient/Maven" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   packaging\client\maven\webapi\clientgeneratorconfigs\genclient.json
 */

import { IVssPageContext, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as Maven from "Feed/Common/Protocols/Maven/Contracts/Maven";
import * as PackagingShared from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";

import { IMavenRestClient } from "Feed/Common/Protocols/Maven/RestClient/Maven.Types";

export { IMavenRestClient } from "Feed/Common/Protocols/Maven/RestClient/Maven.Types";

class MavenRestClient extends RestClientBase implements IMavenRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Fulfills Maven package file download requests by either returning the URL of the requested package file or, in the case of Azure DevOps Server (OnPrem), returning the content as a stream.
     * 
     * @param feedId - Name or ID of the feed.
     * @param groupId - GroupId of the maven package
     * @param artifactId - ArtifactId of the maven package
     * @param version - Version of the package
     * @param fileName - File name to download
     * @param project - Project ID or project name
     */
    public async downloadPackage(
        feedId: string,
        groupId: string,
        artifactId: string,
        version: string,
        fileName: string,
        project?: string
        ): Promise<ArrayBuffer> {

        return this.beginRequest<ArrayBuffer>({
            apiVersion: "6.1-preview.1",
            httpResponseType: "application/octet-stream",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/maven/{groupId}/{artifactId}/{version}/{fileName}/content",
            routeValues: {
                project: project,
                feedId: feedId,
                groupId: groupId,
                artifactId: artifactId,
                version: version,
                fileName: fileName
            }
        });
    }

    /**
     * Update several packages from a single feed in a single request. The updates to the packages do not happen atomically.
     * 
     * @param batchRequest - Information about the packages to update, the operation to perform, and its associated data.
     * @param feedId - Feed which contains the packages to update.
     * @param project - Project ID or project name
     */
    public async updatePackageVersions(
        batchRequest: Maven.MavenPackagesBatchRequest,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/maven/packagesBatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: batchRequest
        });
    }

    /**
     * Delete or restore several package versions from the recycle bin.
     * 
     * @param batchRequest - Information about the packages to update, the operation to perform, and its associated data.
     * @param feed - 
     * @param project - Project ID or project name
     */
    public async updateRecycleBinPackages(
        batchRequest: Maven.MavenPackagesBatchRequest,
        feed: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/packaging/feeds/{feed}/maven/RecycleBin/packagesBatch",
            routeValues: {
                project: project,
                feed: feed
            },
            body: batchRequest
        });
    }

    /**
     * Permanently delete a package from a feed's recycle bin.
     * 
     * @param feed - Name or ID of the feed.
     * @param groupId - Group ID of the package.
     * @param artifactId - Artifact ID of the package.
     * @param version - Version of the package.
     * @param project - Project ID or project name
     */
    public async deletePackageVersionFromRecycleBin(
        feed: string,
        groupId: string,
        artifactId: string,
        version: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feed}/maven/RecycleBin/groups/{groupId}/artifacts/{artifactId}/versions/{version}",
            routeValues: {
                project: project,
                feed: feed,
                groupId: groupId,
                artifactId: artifactId,
                version: version
            }
        });
    }

    /**
     * Get information about a package version in the recycle bin.
     * 
     * @param feed - Name or ID of the feed.
     * @param groupId - Group ID of the package.
     * @param artifactId - Artifact ID of the package.
     * @param version - Version of the package.
     * @param project - Project ID or project name
     */
    public async getPackageVersionMetadataFromRecycleBin(
        feed: string,
        groupId: string,
        artifactId: string,
        version: string,
        project?: string
        ): Promise<Maven.MavenPackageVersionDeletionState> {

        return this.beginRequest<Maven.MavenPackageVersionDeletionState>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feed}/maven/RecycleBin/groups/{groupId}/artifacts/{artifactId}/versions/{version}",
            routeValues: {
                project: project,
                feed: feed,
                groupId: groupId,
                artifactId: artifactId,
                version: version
            }
        });
    }

    /**
     * Restore a package version from the recycle bin to its associated feed.
     * 
     * @param packageVersionDetails - Set the 'Deleted' property to false to restore the package.
     * @param feed - Name or ID of the feed.
     * @param groupId - Group ID of the package.
     * @param artifactId - Artifact ID of the package.
     * @param version - Version of the package.
     * @param project - Project ID or project name
     */
    public async restorePackageVersionFromRecycleBin(
        packageVersionDetails: Maven.MavenRecycleBinPackageVersionDetails,
        feed: string,
        groupId: string,
        artifactId: string,
        version: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feed}/maven/RecycleBin/groups/{groupId}/artifacts/{artifactId}/versions/{version}",
            routeValues: {
                project: project,
                feed: feed,
                groupId: groupId,
                artifactId: artifactId,
                version: version
            },
            body: packageVersionDetails
        });
    }

    /**
     * Get the upstreaming behavior of a package within the context of a feed
     * 
     * @param feed - The name or id of the feed
     * @param groupId - The group id of the package
     * @param artifactId - The artifact id of the package
     * @param project - Project ID or project name
     */
    public async getUpstreamingBehavior(
        feed: string,
        groupId: string,
        artifactId: string,
        project?: string
        ): Promise<PackagingShared.UpstreamingBehavior> {

        return this.beginRequest<PackagingShared.UpstreamingBehavior>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feed}/maven/groups/{groupId}/artifacts/{artifactId}/upstreaming",
            routeValues: {
                project: project,
                feed: feed,
                groupId: groupId,
                artifactId: artifactId
            }
        });
    }

    /**
     * Set the upstreaming behavior of a package within the context of a feed
     * 
     * @param feed - The name or id of the feed
     * @param groupId - 
     * @param artifactId - 
     * @param behavior - The behavior to apply to the package within the scope of the feed
     * @param project - Project ID or project name
     */
    public async setUpstreamingBehavior(
        feed: string,
        groupId: string,
        artifactId: string,
        behavior: PackagingShared.UpstreamingBehavior,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feed}/maven/groups/{groupId}/artifacts/{artifactId}/upstreaming",
            routeValues: {
                project: project,
                feed: feed,
                groupId: groupId,
                artifactId: artifactId
            },
            body: behavior
        });
    }

    /**
     * Get information about a package version.
     * 
     * @param feed - Name or ID of the feed.
     * @param groupId - Group ID of the package.
     * @param artifactId - Artifact ID of the package.
     * @param version - Version of the package.
     * @param project - Project ID or project name
     * @param showDeleted - True to show information for deleted packages.
     */
    public async getPackageVersion(
        feed: string,
        groupId: string,
        artifactId: string,
        version: string,
        project?: string,
        showDeleted?: boolean
        ): Promise<Maven.Package> {

        const queryValues: any = {
            showDeleted: showDeleted
        };

        return this.beginRequest<Maven.Package>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feed}/maven/groups/{groupId}/artifacts/{artifactId}/versions/{version}",
            routeValues: {
                project: project,
                feed: feed,
                groupId: groupId,
                artifactId: artifactId,
                version: version
            },
            queryParams: queryValues
        });
    }

    /**
     * Delete a package version from the feed and move it to the feed's recycle bin.
     * 
     * @param feed - Name or ID of the feed.
     * @param groupId - Group ID of the package.
     * @param artifactId - Artifact ID of the package.
     * @param version - Version of the package.
     * @param project - Project ID or project name
     */
    public async packageDelete(
        feed: string,
        groupId: string,
        artifactId: string,
        version: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feed}/maven/groups/{groupId}/artifacts/{artifactId}/versions/{version}",
            routeValues: {
                project: project,
                feed: feed,
                groupId: groupId,
                artifactId: artifactId,
                version: version
            }
        });
    }

    /**
     * Update state for a package version.
     * 
     * @param packageVersionDetails - Details to be updated.
     * @param feed - Name or ID of the feed.
     * @param groupId - Group ID of the package.
     * @param artifactId - Artifact ID of the package.
     * @param version - Version of the package.
     * @param project - Project ID or project name
     */
    public async updatePackageVersion(
        packageVersionDetails: Maven.PackageVersionDetails,
        feed: string,
        groupId: string,
        artifactId: string,
        version: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feed}/maven/groups/{groupId}/artifacts/{artifactId}/versions/{version}",
            routeValues: {
                project: project,
                feed: feed,
                groupId: groupId,
                artifactId: artifactId,
                version: version
            },
            body: packageVersionDetails
        });
    }

}

export const MavenClientName = "IMavenRestClient";

export function getMavenClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IMavenRestClient {
    return pageContext.getRestClient<IMavenRestClient>(MavenClientName, options);
}

RestClients.add(MavenClientName, { 
    factory: MavenRestClient,
    options: {
        resourceAreaId: "6f7f8c07-ff36-473c-bcf3-bd6cc9b6c066",
        serviceInstanceType: "00000030-0000-8888-8000-000000000000"
    }
});
